import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { gql, useMutation } from '@apollo/client';
import { ResponseWrapper } from 'ui/toast';
import { addNegativeToast, addPositiveToast, removeAllToasts } from 'modules/toastContainer';
import noOp from 'utils/noop';

import {
  CreateSnippetMutationResult,
  GraphQLSnippet,
  SnippetFieldsInput,
  SnippetValidationFailure,
  ValidationData,
} from '../types';
import { getValidationData } from '../utils';

type Variables = {
  name: string;
  fields: SnippetFieldsInput;
};

export const CreateSnippetMutation = gql`
  mutation CreateSnippet($name: String!, $fields: SnippetFieldsInput!) {
    createSnippets(name: $name, fields: $fields) {
      success {
        content
        createdAt
        creatorUserId
        description
        id
        name
        positionalParameters
        projectId
        updatedAt
        updatedBy
      }
      failure {
        __typename
        ... on FieldInvalidFailure {
          field
          errorType {
            __typename
            ... on AnchorTagsWithJS {
              errorMsg
            }
            ... on HasCycle {
              errorMsg
            }
            ... on HasScriptTag {
              errorMsg
            }
            ... on IframeTagWithJS {
              errorMsg
            }
            ... on IllegalCharacters {
              errorMsg
            }
            ... on NotRenderable {
              errorMsg
            }
          }
        }
        ... on FieldNotProvidedFailure {
          field
        }
        ... on FieldTooLongFailure {
          field
        }
        ... on SnippetCreationFailure {
          errorMsg
        }
        ... on SnippetIdDoesNotExistFailure {
          Id
        }
        ... on SnippetNameAlreadyExistsFailure {
          field
        }
        ... on SnippetParameterDuplicateFailure {
          field
        }
      }
    }
  }
`;

type Config = {
  onComplete: () => void;
  onError: (errors: ValidationData[]) => void;
  onSuccess: (snippet: GraphQLSnippet) => void;
};

export function useCreateSnippet({ onComplete = noOp, onError = noOp, onSuccess = noOp }: Config) {
  const dispatch = useDispatch();
  const [createSnippet, data] = useMutation<CreateSnippetMutationResult, Variables>(
    CreateSnippetMutation,
  );

  useEffect(() => {
    // don't do anything until the mutation gets called
    if (!data.called) {
      return;
    }

    if (!data.loading && data.error) {
      dispatch(addNegativeToast('An unknown error occurred')); // check Network tab (possible change to GraphQL schema)
    }

    if (!data.loading && data?.data?.createSnippets) {
      const { failure, success } = data.data.createSnippets;
      const errors: ValidationData[] = [];
      const failures = (Array.isArray(failure) || failure === null ? failure : [failure]) || [];

      failures.forEach((err: SnippetValidationFailure) => {
        const validationData = getValidationData(err);

        dispatch(addNegativeToast(<ResponseWrapper>{validationData.errorMsg}</ResponseWrapper>));
        errors.push(validationData);
      });
      if (errors.length) {
        onError(errors);
      }

      if (success) {
        dispatch(removeAllToasts());
        dispatch(
          addPositiveToast(
            <ResponseWrapper>{`${success.name} was successfully created.`}</ResponseWrapper>,
          ),
        );
        onSuccess(success);
      }

      // call the complete callback
      onComplete();
    }
  }, [data.loading]);

  return [createSnippet, data] as const;
}
